/**
 * SoundSettingsPanel — shared sound controls used in Settings and AppHeader popover.
 *
 * Three-tier trade sounds, defense alerts, and master volume slider.
 */

import { useState } from 'react';
import { Volume2 } from 'lucide-react';
import { Toggle } from '@/components/atoms/Toggle';
import { useSoundSettings } from '@/hooks/ui/useSoundSettings';
import { playFillTone, playWhaleTone, playKaChingTone, playTickTone } from '@/lib/audio/tradeAudio';
import {
  playCautionTone,
  playSweepTone,
  playExposureBreachTone,
  playAdverseFlowTone,
  playQueueFrontTone,
  playBestPriceTone,
  playBootTone,
} from '@/lib/audio/defenseAudio';

interface SoundSettingsPanelProps {
  compact?: boolean;
  onReplayFills?: () => Promise<void>;
}

export function SoundSettingsPanel({ compact = false, onReplayFills }: SoundSettingsPanelProps) {
  const sound = useSoundSettings();
  const [replaying, setReplaying] = useState(false);

  const previewTone = (fn: () => void) => {
    fn();
  };

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {/* Master volume */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Volume2 className="text-muted-foreground h-3.5 w-3.5" />
            <span className={compact ? 'text-xs font-medium' : 'text-sm'}>Master Volume</span>
          </div>
          <span className="text-muted-foreground text-xs tabular-nums">
            {Math.round(sound.volume * 100)}%
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(sound.volume * 100)}
          onChange={(e) => sound.setVolume(Number(e.target.value) / 100)}
          className="accent-primary h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/10 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
        />
      </div>

      {/* Divider */}
      <div className="border-border border-t" />

      {/* Trade sounds */}
      <div className="space-y-2.5">
        <span
          className={`text-muted-foreground ${compact ? 'text-[10px]' : 'text-xs'} font-medium uppercase tracking-wider`}
        >
          Trade Sounds
        </span>
        <div className="flex items-center justify-between">
          <Toggle
            checked={sound.tradeSoundEnabled}
            onChange={sound.setTradeSoundEnabled}
            label="Fill alerts"
            description={compact ? undefined : 'Ping on your order fills'}
            size="sm"
          />
          {sound.tradeSoundEnabled && (
            <button
              onClick={() => previewTone(playFillTone)}
              className="text-muted-foreground hover:text-foreground text-[10px] transition-colors"
            >
              test
            </button>
          )}
        </div>
        {sound.tradeSoundEnabled && (
          <div className="flex items-center gap-2 pl-7">
            <span className="text-muted-foreground text-[10px]">Min contracts:</span>
            <input
              type="number"
              min={0}
              step={1}
              value={sound.minFillContracts}
              onChange={(e) => sound.setMinFillContracts(Number(e.target.value))}
              className="bg-muted border-border w-16 rounded border px-1.5 py-0.5 text-center text-xs tabular-nums"
            />
          </div>
        )}
        <div className="flex items-center justify-between">
          <Toggle
            checked={sound.whaleSoundEnabled}
            onChange={sound.setWhaleSoundEnabled}
            label="Whale alerts"
            description={compact ? undefined : 'Louder ping for 1000+ contract fills'}
            size="sm"
            disabled={!sound.tradeSoundEnabled}
          />
          {sound.whaleSoundEnabled && sound.tradeSoundEnabled && (
            <button
              onClick={() => previewTone(playWhaleTone)}
              className="text-muted-foreground hover:text-foreground text-[10px] transition-colors"
            >
              test
            </button>
          )}
        </div>
        <div className="flex items-center justify-between">
          <Toggle
            checked={sound.firstSaleSoundEnabled}
            onChange={sound.setFirstSaleSoundEnabled}
            label="First sale alerts"
            description={compact ? undefined : 'Ka-ching on first fill of a resting order'}
            size="sm"
            disabled={!sound.tradeSoundEnabled}
          />
          {sound.firstSaleSoundEnabled && sound.tradeSoundEnabled && (
            <button
              onClick={() => previewTone(playKaChingTone)}
              className="text-muted-foreground hover:text-foreground text-[10px] transition-colors"
            >
              test
            </button>
          )}
        </div>
        <div className="flex items-center justify-between">
          <Toggle
            checked={sound.allTradesSound}
            onChange={sound.setAllTradesSound}
            label="All trades tick"
            description={compact ? undefined : 'Soft tick on every market trade'}
            size="sm"
          />
          {sound.allTradesSound && (
            <button
              onClick={() => previewTone(playTickTone)}
              className="text-muted-foreground hover:text-foreground text-[10px] transition-colors"
            >
              test
            </button>
          )}
        </div>
        {onReplayFills && sound.tradeSoundEnabled && (
          <button
            onClick={async () => {
              setReplaying(true);
              try {
                await onReplayFills();
              } finally {
                setReplaying(false);
              }
            }}
            disabled={replaying}
            className="text-muted-foreground hover:text-foreground text-[10px] transition-colors disabled:opacity-50"
          >
            {replaying ? 'replaying…' : 'replay last 3 fills'}
          </button>
        )}
      </div>

      {/* Divider */}
      <div className="border-border border-t" />

      {/* Defense sounds */}
      <div className="space-y-2.5">
        <span
          className={`text-muted-foreground ${compact ? 'text-[10px]' : 'text-xs'} font-medium uppercase tracking-wider`}
        >
          Defense Sounds
        </span>
        <div className="flex items-center justify-between">
          <Toggle
            checked={sound.defenseSoundEnabled}
            onChange={sound.setDefenseSoundEnabled}
            label="Threat alerts"
            description={compact ? undefined : 'Escalation tones for position threats'}
            size="sm"
          />
          {sound.defenseSoundEnabled && (
            <button
              onClick={() => previewTone(playCautionTone)}
              className="text-muted-foreground hover:text-foreground text-[10px] transition-colors"
            >
              test
            </button>
          )}
        </div>
        <div className="flex items-center justify-between">
          <Toggle
            checked={sound.sweepSoundEnabled}
            onChange={sound.setSweepSoundEnabled}
            label="Sweep alerts"
            description={compact ? undefined : 'Rising tone on liquidity sweep detection'}
            size="sm"
            disabled={!sound.defenseSoundEnabled}
          />
          {sound.sweepSoundEnabled && sound.defenseSoundEnabled && (
            <button
              onClick={() => previewTone(playSweepTone)}
              className="text-muted-foreground hover:text-foreground text-[10px] transition-colors"
            >
              test
            </button>
          )}
        </div>
        <div className="flex items-center justify-between">
          <Toggle
            checked={sound.exposureBreachSoundEnabled}
            onChange={sound.setExposureBreachSoundEnabled}
            label="Exposure breach"
            description={compact ? undefined : 'Low pulse when exposure limit is breached'}
            size="sm"
            disabled={!sound.defenseSoundEnabled}
          />
          {sound.exposureBreachSoundEnabled && sound.defenseSoundEnabled && (
            <button
              onClick={() => previewTone(playExposureBreachTone)}
              className="text-muted-foreground hover:text-foreground text-[10px] transition-colors"
            >
              test
            </button>
          )}
        </div>
        <div className="flex items-center justify-between">
          <Toggle
            checked={sound.adverseFlowSoundEnabled}
            onChange={sound.setAdverseFlowSoundEnabled}
            label="Adverse flow"
            description={compact ? undefined : 'Warble on adverse flow pattern escalation'}
            size="sm"
            disabled={!sound.defenseSoundEnabled}
          />
          {sound.adverseFlowSoundEnabled && sound.defenseSoundEnabled && (
            <button
              onClick={() => previewTone(playAdverseFlowTone)}
              className="text-muted-foreground hover:text-foreground text-[10px] transition-colors"
            >
              test
            </button>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="border-border border-t" />

      {/* System sounds */}
      <div className="space-y-2.5">
        <span
          className={`text-muted-foreground ${compact ? 'text-[10px]' : 'text-xs'} font-medium uppercase tracking-wider`}
        >
          System
        </span>
        <div className="flex items-center justify-between">
          <Toggle
            checked={sound.bootSoundEnabled}
            onChange={sound.setBootSoundEnabled}
            label="Boot sound"
            description={compact ? undefined : 'Power-up tone on app load'}
            size="sm"
          />
          {sound.bootSoundEnabled && (
            <button
              onClick={() => previewTone(playBootTone)}
              className="text-muted-foreground hover:text-foreground text-[10px] transition-colors"
            >
              test
            </button>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="border-border border-t" />

      {/* Queue alerts */}
      <div className="space-y-2.5">
        <span
          className={`text-muted-foreground ${compact ? 'text-[10px]' : 'text-xs'} font-medium uppercase tracking-wider`}
        >
          Queue Alerts
        </span>
        <div className="flex items-center justify-between">
          <Toggle
            checked={sound.queueFrontSoundEnabled}
            onChange={sound.setQueueFrontSoundEnabled}
            label="Queue front alert"
            description={compact ? undefined : 'Chime when your order reaches front of queue'}
            size="sm"
          />
          {sound.queueFrontSoundEnabled && (
            <button
              onClick={() => previewTone(playQueueFrontTone)}
              className="text-muted-foreground hover:text-foreground text-[10px] transition-colors"
            >
              test
            </button>
          )}
        </div>
        <div className="flex items-center justify-between">
          <Toggle
            checked={sound.bestPriceSoundEnabled}
            onChange={sound.setBestPriceSoundEnabled}
            label="Best price alert"
            description={compact ? undefined : 'Chirp when market moves to your price level'}
            size="sm"
          />
          {sound.bestPriceSoundEnabled && (
            <button
              onClick={() => previewTone(playBestPriceTone)}
              className="text-muted-foreground hover:text-foreground text-[10px] transition-colors"
            >
              test
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
